import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryParams } from '@/@diffrag/hooks/useQueryParams'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { TableFormatter } from '@/@diffrag/table/TableFormatter'
import { api } from '@/api'
import { IAdminUserFindO1TypeEnum, IAdminUserRoO1StatusEnum } from 'api'
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
const TYPE_OPTIONS = [
  { label: '관리자', value: IAdminUserFindO1TypeEnum.ADMIN },
  { label: '유저', value: IAdminUserFindO1TypeEnum.USER },
  { label: '클리닉', value: IAdminUserFindO1TypeEnum.CLINIC },
]

const NATION_OPTIONS = [
  { label: '전체', value: undefined },
  { label: '한국', value: 'KR' },
  { label: '미국', value: 'US' },
  { label: '중국', value: 'CN' },
  { label: '일본', value: 'JP' },
  { label: '인도', value: 'IN' },
]

const STATUS_OPTIONS = [
  { label: '전체', value: undefined },
  { label: '활동', value: IAdminUserRoO1StatusEnum.ACTIVE },
  { label: '비활성', value: IAdminUserRoO1StatusEnum.SUSPENDED },
  { label: '탈퇴', value: IAdminUserRoO1StatusEnum.WITHDRAWN },
]

export default function User() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', '')
  const { query } = useQueryParams<{
    status?:
      | IAdminUserRoO1StatusEnum.ACTIVE
      | IAdminUserRoO1StatusEnum.SUSPENDED
  }>({
    status: undefined,
  })
  const navigate = useNavigate()
  const [selectedStatus, setSelectedStatus] = useState<
    IAdminUserRoO1StatusEnum | undefined
  >(undefined)
  const [selectedNation, setSelectedNation] = useState<string | undefined>(
    undefined,
  )

  const { data, isFetching } = useApi({
    id: 'userList',
    fn: api().adminUser.adminUserList,
    params: {
      page,
      q,
      type: 'USER' as never,
      status: selectedStatus || query.status,
      nation: selectedNation,
    },
  })

  const selectedStatusLabels = useMemo(() => {
    if (!selectedStatus) {
      return '전체'
    }
    return (
      STATUS_OPTIONS.find((option) => option.value === selectedStatus)?.label ||
      '전체'
    )
  }, [selectedStatus])

  const selectedNationLabels = useMemo(() => {
    if (!selectedNation) {
      return '전체'
    }
    return (
      NATION_OPTIONS.find((option) => option.value === selectedNation)?.label ||
      '전체'
    )
  }, [selectedNation])

  const handleStatusSelect = (status: IAdminUserRoO1StatusEnum | undefined) => {
    setSelectedStatus(status)
  }

  const handleNationSelect = (nation: string | undefined) => {
    setSelectedNation(nation)
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedStatusLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {STATUS_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedStatus === option.value}
                  onCheckedChange={() => handleStatusSelect(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">국적 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedNationLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {NATION_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedNation === option.value}
                  onCheckedChange={() => handleNationSelect(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SimpleTable
        options={{
          disableSelect: true,
          disableNew: true,
          disableSort: true,
        }}
        totalCount={data?.total}
        onRowClick={(row) => {
          navigate(`/users/edit?id=${row.id}`)
        }}
        columns={[
          {
            label: 'NO',
            key: 'id',
            isPrimary: true,
          },
          {
            label: '계정(이메일)',
            key: 'email',
            isPrimary: true,
          },
          {
            label: '이름',
            key: 'userData.name',
            isPrimary: true,
          },
          {
            label: '닉네임',
            key: 'userData.nickname',
            isPrimary: true,
          },
          {
            label: '국적',
            key: 'userData.nation',
          },
          {
            label: '가입일',
            key: 'createDate',
            formatter: TableFormatter.datetime(),
          },
          {
            label: '상태',
            key: 'status',
            valueMap: {
              ACTIVE: '활동',
              SUSPENDED: '비활성',
              WITHDRAWN: '탈퇴',
            },
          },
        ]}
        data={data?.users}
        isLoading={isFetching}
      />
      <OffsetPagination page={page} total={data?.total} pageSize={10} />
    </ContentView>
  )
}
